import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';

// Event the save sites dispatch; NotificationPrimer.tsx (mounted once in
// PersistLogin) listens for it.
export const REMINDER_PRIMER_EVENT = 'doqit:reminder-primer';

export type PrimerPermission = 'granted' | 'prompt' | 'denied' | 'unsupported';

export interface PrimerDetails {
  docName: string;
  // Renewal date as a UTC-midnight Date (see parseRenewalDate).
  renewalDate: Date;
  // The reminder's own date when known.
  reminderDate?: Date | null;
  leadDays?: number | null;
  // Set when the lead time was given in months (reminderDate is then known).
  leadMonths?: number | null;
}

// docName and renewalDate are absent for the generic (no-document) primer
// the day-2 card opens when there is no upcoming reminder to name.
export interface PrimerEventDetail extends Partial<PrimerDetails> {
  // True when the user tapped a card: bypasses the asked flag.
  force?: boolean;
}

export const DEFAULT_LEAD_DAYS = 7;
const DAY_MS = 24 * 60 * 60 * 1000;

export const shouldOfferPrimer = (permission: PrimerPermission, asked: boolean): boolean => (
  permission === 'prompt' && !asked
);

const formatDay = (date: Date): string => new Intl.DateTimeFormat('en-GB', {
  day: 'numeric', month: 'long', timeZone: 'UTC',
}).format(date);

export const primerCopy = ({
  docName, renewalDate, reminderDate, leadDays, leadMonths,
}: PrimerDetails): { title: string; body: string } => {
  let days = leadDays ?? null;
  if (days == null && reminderDate) {
    days = Math.max(0, Math.round((renewalDate.getTime() - reminderDate.getTime()) / DAY_MS));
  }
  if (days == null) days = DEFAULT_LEAD_DAYS;

  const remindOn = reminderDate ?? new Date(renewalDate.getTime() - days * DAY_MS);
  let lead: string;
  if (leadMonths != null && leadMonths > 0) {
    lead = `${leadMonths} ${leadMonths === 1 ? 'month' : 'months'} before`;
  } else if (days === 0) {
    lead = 'the day';
  } else {
    lead = `${days} ${days === 1 ? 'day' : 'days'} before`;
  }
  const sentence = lead === 'the day'
    ? `That's the day ${docName} runs out.`
    : `That's ${lead} ${docName} runs out.`;
  return {
    title: `We'll remind you on ${formatDay(remindOn)}`,
    body: `${sentence} Allow notifications so the reminder reaches you.`,
  };
};

export const GENERIC_PRIMER_COPY = {
  title: 'Let Doqit remind you',
  body: 'A heads-up before your insurance, MOT or passport runs out, so nothing catches you out. Allow notifications so the reminders reach you.',
} as const;

/** What the mock notification on the primer shows: the kind of thing they will get. */
export const primerPreviewForEvent = (detail: PrimerEventDetail): { title: string; body: string } => (
  detail.docName && detail.renewalDate
    ? { title: detail.docName, body: `Runs out on ${formatDay(detail.renewalDate)}. Time to sort it.` }
    : { title: 'Car insurance', body: 'Renews in 14 days. Time to compare quotes.' }
);

export const primerCopyForEvent = (detail: PrimerEventDetail): { title: string; body: string } => (
  detail.docName && detail.renewalDate
    ? primerCopy(detail as PrimerDetails)
    : { ...GENERIC_PRIMER_COPY }
);

/**
 * The generic primer, for a signed-in member with no dated reminder yet. Not
 * forced: it shows once per account (the asked flag) and only while the
 * system permission has never been asked. Waiting for a dated save meant
 * anyone who never added one was never asked at all.
 */
export const requestGenericPrimer = (): void => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent<PrimerEventDetail>(REMINDER_PRIMER_EVENT, { detail: {} }));
};

export const requestReminderPrimer = (details: PrimerDetails): void => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent<PrimerEventDetail>(REMINDER_PRIMER_EVENT, { detail: details }));
};

export const getPrimerState = async (): Promise<PrimerPermission> => {
  if (!Capacitor.isNativePlatform()) return 'unsupported';
  try {
    const { display } = await LocalNotifications.checkPermissions();
    if (display === 'granted') return 'granted';
    if (display === 'denied') return 'denied';
    return 'prompt';
  } catch {
    return 'unsupported';
  }
};

// For a card the user tapped (day-2 card). Shows the primer straight away,
// ignoring the asked flag. Only meaningful for 'prompt'; the app has no
// open-settings pattern yet, so 'denied' is not handled here. Without details
// the primer falls back to generic copy.
export const openPrimerFromCard = (details?: PrimerDetails | null): void => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent<PrimerEventDetail>(REMINDER_PRIMER_EVENT, {
    detail: { ...details, force: true },
  }));
};

export const primerAskedKey = (userId: string | number): string => `doqit_lifecycle_primer_asked_${userId}`;

// Whether the primer is on screen right now. The tour auto-start reads this
// so the two never stack; PRIMER_OPEN_CHANGED_EVENT lets it re-check once the
// primer closes.
export const PRIMER_OPEN_CHANGED_EVENT = 'doqit:reminder-primer-open-changed';
let primerOpen = false;

export const isPrimerOpen = (): boolean => primerOpen;

export const setPrimerOpen = (open: boolean): void => {
  if (primerOpen === open) return;
  primerOpen = open;
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent<boolean>(PRIMER_OPEN_CHANGED_EVENT, { detail: open }));
};
